<template>
  <div class="scope-selector" v-if="isRoleAdministrator">
    <LabeledSelect
      id="spaces"
      class="sm"
      name="spaces"
      :label="$t('app.panels.scopeSelector.space.label')"
      :model-value="undefined"
      :placeholder="$t('app.panels.scopeSelector.space.placeholder')"
      :searchable="false"
      :clearable="false"
      :options="spaces"
      @update:modelValue="updateAdminSpaceList"
    >
      <template #selected-option="option: Space">
        <div class="xs-text">
          {{ option.name }}
        </div>
      </template>
      <template #option="option: Space">
        <div class="xs-text">
          {{ option.name }}
        </div>
      </template>
    </LabeledSelect>
    <div class="scope-list">
      <div v-for="space in resolvedSpaceScopes" class="scope">
        <p>{{ space.name }}</p>
        <i class="bi bi-x-lg" @click="removeSpace(space)"></i>
      </div>
    </div>
  </div>
  <div class="scope-selector" v-else-if="isRoleConsultant">
    <LabeledSelect
      v-if="allCampaigns"
      id="campaigns"
      class="sm"
      name="campaigns"
      :label="$t('app.panels.scopeSelector.campaign.label')"
      :model-value="undefined"
      :placeholder="$t('app.panels.scopeSelector.campaign.placeholder')"
      :searchable="false"
      :clearable="false"
      :options="allCampaigns"
      @update:modelValue="updateConsultantCampaignList"
    >
      <template #selected-option="option: Campaign">
        <div class="xs-text">
          {{ option.campaign.internal_name }}
        </div>
      </template>
      <template #option="option: Campaign">
        <div class="xs-text">
          {{ option.campaign.internal_name }}
        </div>
      </template>
    </LabeledSelect>
    <div class="scope-list">
      <div v-for="campaign in resolvedCampaignScopes" class="scope">
        <p>{{ campaign.campaign.internal_name }}</p>
        <i class="bi bi-x-lg" @click="removeCampaign(campaign)"></i>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import LabeledSelect from '@/components/global/LabeledSelect.vue';
import { fetchCampaigns } from '@/queries/campaigns.query';
import { fetchSpaces, useSpaces } from '@/queries/space.query';
import { Campaign } from '@/types/campaign';
import { ScopeRef, ScopeRefKind } from '@/types/member';
import { AppRole, DefaultAppRole, MemberRole } from '@/types/role';
import { Space } from '@/types/space';

const appStore = useAppStore();
const { data: spaces, isPending: isSpacesPending } = useSpaces();

const allCampaigns: Ref<Campaign[]> = ref([]);

const selectedScopes = ref<ScopeRef[]>([]);

const emit = defineEmits<{
  (e: 'selectedScopes', value: ScopeRef[]): void,
}>();

const props = defineProps<{
  role: AppRole,
  scopes: ScopeRef[],
}>();

watch(props, () => {
  selectedScopes.value = props?.scopes || [];
}, { deep: true })

onMounted(async () => {
  selectedScopes.value = props?.scopes || [];
  const spaces = await fetchSpaces(appStore.currentOrganisationId);
  for (const space of spaces) {
    allCampaigns.value.push(...(await fetchCampaigns(appStore.currentOrganisationId, space.workspace_id)));
  }
})

const isRoleConsultant = computed(() => {
  if (props.role?.name === DefaultAppRole.CONSULTANT) return true;
});

const isRoleAdministrator = computed(() => {
  if (props.role?.name === DefaultAppRole.ADMINISTRATOR) return true;
});

const resolvedCampaignScopes = computed(() => {
  return allCampaigns.value.filter((c) => selectedScopes.value.some((scope) => scope.id === c.id ));
});

const resolvedSpaceScopes = computed(() => {
  return spaces.value.filter((c) => selectedScopes.value.some((scope) => scope.id === c.workspace_id ));
});

function updateAdminSpaceList(newSpace: Space) {
  selectedScopes.value.push({ kind: ScopeRefKind.WORKSPACE, id: newSpace.workspace_id});
  emit("selectedScopes", selectedScopes.value);
}

function removeSpace(toDelete: Space) {
  selectedScopes.value = selectedScopes.value.filter((s) => {
    return s.id !== toDelete.workspace_id;
  });
  emit("selectedScopes", selectedScopes.value);
}

function updateConsultantCampaignList(newCampaign: Campaign) {
  selectedScopes.value.push({ kind: ScopeRefKind.CAMPAIGN, id: newCampaign.id});
  emit("selectedScopes", selectedScopes.value);
}

function removeCampaign(toDelete: Campaign) {
  selectedScopes.value = selectedScopes.value.filter((c) => {
    return c.id !== toDelete.id;
  });
  emit("selectedScopes", selectedScopes.value);
}
</script>

<style scoped lang="scss">
@import "@/assets/style/variables.scss";

.label {
  font-size: 12px;
  color: var(--label-color);
  margin-bottom: 6px;
}

.scope-selector {
  display: flex;
  flex-direction: column;
  gap: 16px;

  .scope-list {
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;

    .scope {
      padding: 6px 8px;
      background-color: var(--light-grey-color);
      color: var(--secondary-color);
      border-radius: 4px;

      display: flex;
      gap: 6px;
      align-items: center;
      justify-content: center;

      i {
        cursor: pointer;
      }
    }
  }
}
</style>